import { Typography } from 'antd'
import { ConversationList, Conversation } from '@chatscope/chat-ui-kit-react'
import { DeleteOutlined } from '@ant-design/icons'
import { Pto } from '@outcoreaix/pto'
import { FC } from 'react'
import { ConfirmModal } from 'components'
import { useQueryParams } from 'hooks/useQueryParams'

const defaultChatName = 'Empty chat'

type Props = {
  chats?: Pto.Chats.ChatList
  onChatClick: (id: number) => void
  createChat: () => Promise<void | Pto.Chats.Chat>
  deleteChat: (chatId: number) => Promise<void>
  activeChatId?: number
  isLoading?: boolean
  disabled?: boolean
  onClick?: () => void
  isCollapsed?: boolean
  id?: string
}

const ChatMenuContent: FC<Props> = ({
  chats,
  onChatClick,
  createChat,
  activeChatId,
  deleteChat,
  disabled,
  isLoading,
  onClick,
  isCollapsed,
  id
}) => {
  const { removeParam } = useQueryParams()

  const handleClick = (chatId: number) => {
    removeParam(Pto.App.ParamKeyEnum.FlowId)
    onChatClick(chatId)
    onClick?.()
  }

  const isEmpty = !chats?.items.length

  return (
    <div id={id} className="flex-1 min-h-0 overflow-hidden mb-8">
      {isEmpty && !isCollapsed ? (
        // Render the placeholder instead of the (empty) ConversationList: the list stretches to fill
        // the flex-1 column, which would push a sibling placeholder out of the overflow-hidden box.
        <div className="mx-2 rounded-xl px-4 py-3 border border-dashed border-gray-300">
          <Typography.Text className="text-gray-500">All chats will show up here once started</Typography.Text>
        </div>
      ) : (
        <ConversationList
          className={`flex-1 overflow-auto min-h-0 scrollbar-thin ${isCollapsed ? 'hidden' : 'visible'}`}
        >
          {chats?.items.map((chat) => (
            <Conversation
              key={`chat_${chat.id}`}
              active={chat.id === activeChatId}
              info={
                chat.status !== Pto.Chats.ChatStatus.Active ? (
                  <Typography style={{ fontSize: '10px', color: '#ff914d' }}>{chat.status}</Typography>
                ) : undefined
              }
              className="mb-1.5 w-full items-center !p-0"
              onClick={() => handleClick(chat.id)}
            >
              <Conversation.Content className={'pl-1 mr-0'}>
                <Typography.Text className="text-gray-500 whitespace-nowrap text-ellipsis overflow-hidden">
                  {chat.name || defaultChatName}
                </Typography.Text>
              </Conversation.Content>
              <Conversation.Operations
                className="max-xl:!visible mr-3"
                key={`chat_delete_${chat.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <ConfirmModal
                  button={{ icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />, label: '', size: 'middle' }}
                  title="Are you sure you want delete this chat?"
                  className="!px-2"
                  onOk={() => {
                    void deleteChat(chat.id)
                  }}
                />
              </Conversation.Operations>
            </Conversation>
          ))}
        </ConversationList>
      )}
    </div>
  )
}

export default ChatMenuContent
